import { useCallback, useState } from 'react'
import { schedulerService, describeCron, type ScheduleConfig } from '../services/scheduler'
import { Clock, RefreshCw, Pencil, X, ToggleLeft, ToggleRight, ChevronDown } from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { useWindowWidth } from '../../../lib/useWindowWidth'

// ── Frequency presets ─────────────────────────────────────────────────────────
// Each preset maps to the cron fields accepted by the backend.

interface Preset {
  label: string
  minute: string
  hour: string
  day_of_week: string
  day_of_month: string
  month_of_year: string
}

const PRESETS: Preset[] = [
  { label: 'Every 15 minutes', minute: '*/15', hour: '*',  day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Every 30 minutes', minute: '*/30', hour: '*',  day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Every hour',       minute: '0',    hour: '*',  day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Every 2 hours',    minute: '0',    hour: '*/2', day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Every 6 hours',    minute: '0',    hour: '*/6', day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Every 12 hours',   minute: '0',    hour: '*/12', day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Daily at midnight',minute: '0',    hour: '0',  day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Daily at 2 am',    minute: '0',    hour: '2',  day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Daily at 6 am',    minute: '0',    hour: '6',  day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Daily at 11 pm',   minute: '0',    hour: '23', day_of_week: '*', day_of_month: '*', month_of_year: '*' },
  { label: 'Weekly (Sun midnight)', minute: '0', hour: '0', day_of_week: '0', day_of_month: '*', month_of_year: '*' },
]

/** Returns the matching preset label or 'custom' if no preset matches. */
function cronToPresetLabel(cfg: ScheduleConfig): string {
  const match = PRESETS.find(
    p =>
      p.minute        === cfg.minute &&
      p.hour          === cfg.hour &&
      p.day_of_week   === cfg.day_of_week &&
      p.day_of_month  === cfg.day_of_month &&
      p.month_of_year === cfg.month_of_year,
  )
  return match ? match.label : 'custom'
}

// ── Edit Modal ────────────────────────────────────────────────────────────────

function ScheduleEditModal({
  cfg,
  onClose,
  onSaved,
}: {
  cfg: ScheduleConfig
  onClose: () => void
  onSaved: (updated: ScheduleConfig) => void
}) {
  const initialPreset = cronToPresetLabel(cfg)
  const [presetLabel, setPresetLabel] = useState(initialPreset)
  const [custom, setCustom] = useState({
    minute:        cfg.minute,
    hour:          cfg.hour,
    day_of_week:   cfg.day_of_week,
    day_of_month:  cfg.day_of_month,
    month_of_year: cfg.month_of_year,
  })
  const [is_enabled, setEnabled] = useState(cfg.is_enabled)
  const [saving, setSaving]       = useState(false)
  const [error, setError]         = useState('')

  const isCustom = presetLabel === 'custom'

  // Current cron fields — either from the selected preset or the custom inputs
  const cronFields = isCustom
    ? custom
    : PRESETS.find(p => p.label === presetLabel)!

  const handlePresetChange = (label: string) => {
    setPresetLabel(label)
    if (label !== 'custom') {
      const p = PRESETS.find(p => p.label === label)!
      setCustom({ minute: p.minute, hour: p.hour, day_of_week: p.day_of_week,
                  day_of_month: p.day_of_month, month_of_year: p.month_of_year })
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setSaving(true); setError('')
    try {
      const updated = await schedulerService.updateSchedule(cfg.task_key, {
        ...cronFields, is_enabled,
      })
      onSaved(updated)
      onClose()
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'Failed to save — check that cron fields are valid.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel" style={{ maxWidth: 460 }}>
        <div className="modal-header">
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>
              Edit Schedule
            </h2>
            <p className="text-sm mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              {cfg.display_name}
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {cfg.description && (
            <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{cfg.description}</p>
          )}

          {/* Frequency picker */}
          <div className="form-group">
            <label className="form-label">Run frequency</label>
            <select
              className="input"
              value={presetLabel}
              onChange={e => handlePresetChange(e.target.value)}
            >
              {PRESETS.map(p => (
                <option key={p.label} value={p.label}>{p.label}</option>
              ))}
              <option value="custom">Custom…</option>
            </select>
          </div>

          {/* Custom cron fields — only shown when "Custom" is selected */}
          {isCustom && (
            <div className="rounded-lg p-3 space-y-3"
              style={{ background: 'var(--surface-subtle)', border: '1px solid var(--border-subtle)' }}>
              <p className="text-xs font-medium" style={{ color: 'var(--text-tertiary)' }}>
                Advanced — cron fields (minute / hour / day-of-week / day-of-month / month)
              </p>
              <div className="grid grid-cols-2 gap-3">
                {([
                  ['minute',        'Minute',        '0  |  */15  |  30'],
                  ['hour',          'Hour',           '*  |  */6   |  23'],
                  ['day_of_week',   'Day of week',    '* = every day  |  0=Sun … 6=Sat'],
                  ['day_of_month',  'Day of month',   '* = every day  |  1–31'],
                  ['month_of_year', 'Month',          '* = every month  |  1–12'],
                ] as const).map(([field, label, hint]) => (
                  <div className="form-group" key={field}>
                    <label className="form-label">{label}</label>
                    <input
                      className="input font-mono"
                      value={custom[field]}
                      onChange={e => setCustom(c => ({ ...c, [field]: e.target.value }))}
                      placeholder={hint}
                      required
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Human-readable preview */}
          <div className="flex items-center gap-2 rounded-md px-3 py-2 text-sm"
            style={{ background: 'var(--info-bg)', color: 'var(--info-text)' }}>
            <Clock className="h-3.5 w-3.5 shrink-0" />
            <span>{describeCron(cronFields)}</span>
          </div>

          {/* Enabled toggle */}
          <label className="flex items-center gap-3 cursor-pointer select-none">
            <div
              onClick={() => setEnabled(v => !v)}
              className="flex items-center"
              role="checkbox"
              aria-checked={is_enabled}
            >
              {is_enabled
                ? <ToggleRight className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
                : <ToggleLeft  className="h-6 w-6" style={{ color: 'var(--text-tertiary)' }} />
              }
            </div>
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
              {is_enabled ? 'Enabled — task will run on schedule' : 'Paused — task will not run'}
            </span>
          </label>

          {error && <div className="alert-danger text-sm">{error}</div>}

          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Apply'}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function SchedulerPage() {
  const [configs, setConfigs]   = useState<ScheduleConfig[]>([])
  const [loading, setLoading]   = useState(true)
  const [editing, setEditing]   = useState<ScheduleConfig | null>(null)
  const [toggling, setToggling] = useState<string | null>(null)
  const [error, setError]       = useState('')
  const [expandedKey, setExpandedKey] = useState<string | null>(null)
  const isMobile = useWindowWidth() < 768

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      setConfigs(await schedulerService.listSchedules())
    } catch {
      setError('Failed to load scheduled tasks')
    } finally {
      setLoading(false)
    }
  }, [])

  useState(() => { load() })

  const handleToggle = async (cfg: ScheduleConfig) => {
    setToggling(cfg.task_key)
    try {
      const updated = await schedulerService.updateSchedule(cfg.task_key, {
        is_enabled: !cfg.is_enabled,
      })
      setConfigs(prev => prev.map(c => c.task_key === updated.task_key ? updated : c))
    } catch {
      setError('Failed to update task status')
    } finally {
      setToggling(null)
    }
  }

  return (
    <div className="space-y-5">
      <div className="section-header">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Clock className="h-5 w-5" style={{ color: 'var(--brand-primary)' }} />
            Scheduled Tasks
            <button onClick={load} className="btn-icon" title="Refresh" disabled={loading} style={{ marginLeft: 'auto' }}>
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </h1>
          <p className="page-subtitle">
            Control when automated tasks run. Changes take effect within 30 seconds — no restart needed.
          </p>
        </div>
      </div>

      {error && <div className="alert-danger">{error}</div>}

      {isMobile ? (
        loading ? (
          <div className="py-10 flex justify-center"><div className="spinner" /></div>
        ) : configs.length === 0 ? (
          <div className="empty-state"><p style={{ color: 'var(--text-tertiary)' }}>No scheduled tasks found</p></div>
        ) : (
          <div className="flex flex-col gap-2">
            {configs.map(cfg => {
              const expanded = expandedKey === cfg.task_key
              return (
                <div key={cfg.task_key} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div
                    className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
                    onClick={() => setExpandedKey(expanded ? null : cfg.task_key)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{cfg.display_name}</p>
                      <span className="text-xs font-medium" style={{ color: 'var(--brand-primary)' }}>{describeCron(cfg)}</span>
                    </div>
                    <button
                      className="flex items-center gap-1 text-xs shrink-0"
                      style={{
                        color: cfg.is_enabled ? 'var(--success-text)' : 'var(--text-tertiary)',
                        background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px',
                      }}
                      onClick={e => { e.stopPropagation(); handleToggle(cfg) }}
                      disabled={toggling === cfg.task_key}
                    >
                      {cfg.is_enabled ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                      {toggling === cfg.task_key ? '…' : cfg.is_enabled ? 'Active' : 'Paused'}
                    </button>
                    <ChevronDown
                      className="h-4 w-4 shrink-0 transition-transform"
                      style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
                    />
                  </div>
                  {expanded && (
                    <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                      {cfg.description && (
                        <p className="text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>{cfg.description}</p>
                      )}
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Last modified</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>
                          {cfg.updated_at
                            ? new Date(cfg.updated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
                            : 'Never'}
                        </span>
                      </div>
                      <button
                        className="w-full flex items-center justify-center gap-1.5 text-xs py-2 rounded-md border mt-3"
                        style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)', background: 'transparent' }}
                        onClick={e => { e.stopPropagation(); setEditing(cfg) }}
                      >
                        <Pencil className="h-3.5 w-3.5" /> Change Schedule
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )
      ) : (
        <div className="table-card">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Description</th>
                  <th>Runs</th>
                  <th>Status</th>
                  <th>Modified</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center" style={{ color: 'var(--text-tertiary)' }}>
                      Loading…
                    </td>
                  </tr>
                ) : configs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-10 text-center" style={{ color: 'var(--text-tertiary)' }}>
                      No scheduled tasks found
                    </td>
                  </tr>
                ) : configs.map(cfg => (
                  <tr key={cfg.task_key}>
                    <td>
                      <p className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                        {cfg.display_name}
                      </p>
                    </td>
                    <td style={{ maxWidth: 240 }}>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {cfg.description || '—'}
                      </p>
                    </td>
                    <td>
                      <span className="font-medium text-sm" style={{ color: 'var(--brand-primary)' }}>
                        {describeCron(cfg)}
                      </span>
                    </td>
                    <td>
                      <button
                        className="flex items-center gap-1.5 text-xs"
                        style={{
                          color: cfg.is_enabled ? 'var(--success-text)' : 'var(--text-tertiary)',
                          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                        }}
                        onClick={() => handleToggle(cfg)}
                        disabled={toggling === cfg.task_key}
                        title={cfg.is_enabled ? 'Click to pause' : 'Click to enable'}
                      >
                        {cfg.is_enabled
                          ? <ToggleRight className="h-4 w-4" />
                          : <ToggleLeft  className="h-4 w-4" />
                        }
                        {toggling === cfg.task_key ? 'Saving…' : cfg.is_enabled ? 'Active' : 'Paused'}
                      </button>
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap' }}>
                      {cfg.updated_at
                        ? new Date(cfg.updated_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })
                        : 'Never'}
                    </td>
                    <td>
                      <button className="btn-icon" title="Change schedule" onClick={() => setEditing(cfg)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && (
        <ScheduleEditModal
          cfg={editing}
          onClose={() => setEditing(null)}
          onSaved={updated => {
            setConfigs(prev => prev.map(c => c.task_key === updated.task_key ? updated : c))
            setEditing(null)
          }}
        />
      )}
    </div>
  )
}
